// One wizard step shell (spec §8): every step skippable, same shell for
// both onboarding doors. Originally typed `step: LayerKey` (the onboarding
// wizard's four layers); widened to `string` (WP3b, D4) so other wizards —
// e.g. the domain & mailbox wizard — can reuse this exact chrome with their
// own step keys. `step` is display/labeling only (aria id + "step N of T ·
// {step}" text), so the widening is behavior-preserving for every existing
// caller.
import type { ReactNode } from 'react';

export interface WizardStepProps {
  step: string;
  /** 1-based position and total — renders "step 2 of 4 · visibility". */
  index: number;
  total: number;
  title: string;
  children: ReactNode;
  onContinue: () => void;
  continueLabel?: string;
  /** Spec §8: every wizard step is skippable — the wizard host ALWAYS passes
   *  this; it is optional only so landing screens can reuse the shell. */
  onSkip?: () => void;
  skipLabel?: string;
}

export function WizardStep({
  step,
  index,
  total,
  title,
  children,
  onContinue,
  continueLabel = 'continue',
  onSkip,
  skipLabel = 'skip for now',
}: WizardStepProps) {
  return (
    <section aria-labelledby={`wizard-step-${step}`} data-step={step} className="mx-auto max-w-xl">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        step {index} of {total} · {step}
      </p>
      <h2 id={`wizard-step-${step}`} className="mt-1 text-xl font-bold text-text-primary">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          onClick={onContinue}
          className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
        >
          {continueLabel}
        </button>
        {onSkip ? (
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-text-tertiary hover:text-text-secondary"
          >
            {skipLabel}
          </button>
        ) : null}
      </div>
    </section>
  );
}
